import { Button, Container, HStack, Icon, Text } from "@chakra-ui/react"
import { createFileRoute } from "@tanstack/react-router"
import { useMemo, useState } from "react"
import {
  LuBookmarkMinus,
  LuCheckCircle2,
  LuFileX,
  LuXCircle,
} from "react-icons/lu"
import { SiSpeedtest } from "react-icons/si"
import { startCeleryTask } from "../../../api/actions"
import { SettingsActionsService } from "../../../client"
import TabsList from "../../../components/Common/TabsList"
import { NoPermission } from "../../../components/UI/no-permission"
import { userHasPermission } from "../../../utils"

export const Route = createFileRoute("/_layout/settings/actions")({
  component: Actions,
})

function ActionsControlPanel() {
  // Permission check
  const [ac_execute] = useMemo(() => {
    return ["execute"].map(
      (action) =>
        userHasPermission([
          { perm: `settings_actions_${action}`, is_allowed: false },
        ])[0].is_allowed,
    )
  }, [userHasPermission])

  if (!ac_execute) return <NoPermission />

  const celeryPublicCache = startCeleryTask("settings-actions-public", () =>
    SettingsActionsService.actionClearPublicCache(),
  )
  const celeryPrivateCache = startCeleryTask("settings-actions-private", () =>
    SettingsActionsService.actionClearPrivateCache(),
  )
  const celeryWholeCache = startCeleryTask("settings-actions-cache", () =>
    SettingsActionsService.actionClearWholeCache(),
  )
  const celeryPruneMedia = startCeleryTask("settings-actions-media", () =>
    SettingsActionsService.actionPruneMedia(),
  )

  const [sentryCheck, setSentryCheck] = useState<boolean>(false)

  return (
    <Container maxW="full" my={10}>
      <HStack mt={3}>
        <Text>RestAPI: Löschen öffentlicher Cache</Text>
        {celeryPublicCache.taskSuccess !== null &&
          (celeryPublicCache.taskSuccess ? (
            <Icon data-testid="delete-public-cache-status" as={LuCheckCircle2} size="md" color="success" />
          ) : (
            <Icon data-testid="delete-public-cache-status-fail" as={LuXCircle} size="md" color="danger" />
          ))}
      </HStack>
      <Button
        data-testid="delete-public-cache"
        onClick={celeryPublicCache.taskStatus}
        loading={celeryPublicCache.loading}
        variant="primary"
      >
        <LuBookmarkMinus />
      </Button>

      <HStack mt={3}>
        <Text>RestAPI: Löschen privater Cache</Text>
        {celeryPrivateCache.taskSuccess === true && (
          <Icon data-testid="delete-private-cache-status" as={LuCheckCircle2} size="md" color="success" />
        )}

        {celeryPrivateCache.taskSuccess === false && (
          <Icon data-testid="delete-private-cache-status-fail" as={LuXCircle} size="md" color="danger" />
        )}
      </HStack>
      <Button
        data-testid="delete-private-cache"
        onClick={celeryPrivateCache.taskStatus}
        loading={celeryPrivateCache.loading}
        variant="primary"
      >
        <LuBookmarkMinus />
      </Button>

      <HStack mt={3}>
        <Text>RestAPI: Löschen kompletter Cache</Text>
        {celeryWholeCache.taskSuccess === true && (
          <Icon data-testid="delete-whole-cache-status" as={LuCheckCircle2} size="md" color="success" />
        )}

        {celeryWholeCache.taskSuccess === false && (
          <Icon data-testid="delete-whole-cache-status-fail" as={LuXCircle} size="md" color="danger" />
        )}
      </HStack>
      <Button
        data-testid="delete-whole-cache"
        onClick={celeryWholeCache.taskStatus}
        loading={celeryWholeCache.loading}
        variant="primary"
      >
        <LuBookmarkMinus />
      </Button>

      <HStack mt={3}>
        <Text>Mediendateien aufräumen</Text>
        {celeryPruneMedia.taskSuccess === true && (
          <Icon data-testid="prune-media-status" as={LuCheckCircle2} size="md" color="success" />
        )}

        {celeryPruneMedia.taskSuccess === false && (
          <Icon data-testid="prune-media-status-fail" as={LuXCircle} size="md" color="danger" />
        )}
      </HStack>
      <Button
        data-testid="prune-media"
        onClick={celeryPruneMedia.taskStatus}
        loading={celeryPruneMedia.loading}
        variant="primary"
      >
        <LuFileX />
      </Button>

      <HStack mt={3}>
        <Text>Sentry: Verbindung testen</Text>
        {sentryCheck &&
          <Icon data-testid="prune-media-status" as={LuCheckCircle2} size="md" color="success" />
        }
      </HStack>
      <Button
        marginTop={3}
        onClick={() => {
          setSentryCheck(true)
          throw new Error("Sentry test error for Reda!")
        }}
        variant="primary"
      >
        <SiSpeedtest />
      </Button>
    </Container>
  )
}

export default ActionsControlPanel

function Actions() {
  const tabs = useMemo(() => [
    { value: "settings", title: "Allgemein" },
    { value: "settings/navigation", title: "Navigation" },
    { value: "settings/landingpage", title: "Startseite" },
    { value: "settings/mimetypes", title: "Dateitypen" },
    { value: "settings/subscriptions", title: "Abos" },
    { value: "settings/priocategories", title: "Kategorien" },
    { value: "settings/ratecharts", title: "Tariftabellen" },
    { value: "settings/actions", title: "Aktionen" },
  ], []
  )

  return <TabsList tabs={tabs} defaultVal="settings" />
}
